import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { Campaign } from '../../services/campaign.service';
import { ConfirmActionModalComponent } from '../../shared/components/confirm-action-modal';

/**
 * Modal component that prompts the user to confirm stopping a campaign and emits actions for confirm or cancel.
 * @component
 * @selector app-stop-campaign-modal
 * @example
 *   <app-stop-campaign-modal [campaign]="sampleCampaign" (confirmStop)="handleConfirm($event)" (close)="handleClose()"></app-stop-campaign-modal>
 * @input {Campaign | null} campaign - The campaign to be stopped; used to display details and passed back on confirmation. May be null if no campaign is selected.
 * @output {EventEmitter<void>} close - Emitted when the modal is dismissed or the user cancels the stop action.
 * @output {EventEmitter<Campaign>} confirmStop - Emitted when the user confirms stopping the campaign; emits the current campaign as the payload.
 *
 * Key methods:
 * - onClose(): void - Invoked to close the modal; emits the close event.
 * - onConfirmStop(): void - Invoked when the user confirms stopping; if a campaign is present, emits the confirmStop event with the campaign.
 *
 * Lifecycle hooks:
 * - None used internally by this component. No special initialization or teardown is required.
 */
@Component({
  selector: 'app-stop-campaign-modal',
  standalone: true,
  imports: [ConfirmActionModalComponent],
  templateUrl: './stop-campaign-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./stop-campaign-modal.component.scss'],
})
export class StopCampaignModalComponent {
  @Input() campaign: Campaign | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() confirmStop = new EventEmitter<Campaign>();

  onClose(): void {
    this.close.emit();
  }

  onConfirmStop(): void {
    if (this.campaign) {
      this.confirmStop.emit(this.campaign);
    }
  }
}
